import { useCallback, useEffect, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import { apiClient } from '@/api/client'
import { formatDateTime } from '@/utils/date'
import { Button } from '@/components/ui/button'
import { OwnerPicker, useAiOwners } from '@/pages/admin/OwnerPicker'

// AI-RISK-ACTIONS-1 — what we will do about a risk on the AI Register: one
// owner, an optional due date, a status. A record only: it blocks, allows and
// changes nothing about how AI runs. Nothing is deleted; edit to correct.

interface RiskAction {
  id: string
  feature_key: string
  feature_name: string
  text: string
  owner: string
  due: string
  status: string
  overdue: boolean
  updated_at: string
  updated_by: string
}

interface Report {
  actions: RiskAction[]
  features: { key: string; name: string }[]
  summary?: { open: number; in_progress: number; done: number; accepted: number; overdue: number }
}

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
  { value: 'accepted', label: 'Accepted' },
]

const BASE = '/v1/admin/ai-risk-actions'

function errorText(err: unknown): string {
  const e = err as { response?: { data?: { detail?: unknown } }; message?: string }
  const detail = e?.response?.data?.detail
  return typeof detail === 'string' ? detail : e?.message || 'Save failed.'
}

function ActionRow({ a, error, onSave }: {
  a: RiskAction
  error?: string
  onSave: (id: string, status: string) => void
}) {
  const [status, setStatus] = useState(a.status)
  useEffect(() => { setStatus(a.status) }, [a.status])
  return (
    <tr data-testid={`ra-row-${a.id}`} className="border-t align-top">
      <td className="py-2 pr-2">
        {a.feature_name}
        {a.overdue && (
          <span data-testid={`ra-overdue-${a.id}`}
            className="ml-2 inline-flex rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">Overdue</span>
        )}
      </td>
      <td className="py-2 pr-2">{a.text}</td>
      <td className="py-2 pr-2">{a.owner}</td>
      <td className="py-2 pr-2">{a.due || 'No due date'}</td>
      <td className="py-2 pr-2">
        <select data-testid={`ra-status-${a.id}`} className="rounded border px-2 py-1" value={status}
          onChange={e => setStatus(e.target.value)}>
          {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <span className="mt-1 block text-xs text-gray-500">
          Updated {formatDateTime(a.updated_at)} by {a.updated_by}
        </span>
        {error && <span data-testid={`ra-error-${a.id}`} className="mt-1 block text-sm text-red-600">{error}</span>}
      </td>
      <td className="py-2">
        <Button type="button" size="sm" data-testid={`ra-save-${a.id}`} onClick={() => onSave(a.id, status)}>Save</Button>
      </td>
    </tr>
  )
}

export function AiRiskActionsSection() {
  const { data, loading, error, execute } = useApi<Report>()
  const [report, setReport] = useState<Report | null>(null)
  const [form, setForm] = useState({ feature_key: '', text: '', owner: '', due: '' })
  const [formError, setFormError] = useState('')
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({})
  const owners = useAiOwners()

  const load = useCallback(
    () => execute(() => apiClient.get<Report>(BASE).then(res => res.data)),
    [execute],
  )
  useEffect(() => { load() }, [load])
  useEffect(() => { if (data) setReport(data) }, [data])

  const features = report?.features ?? []
  const actions = report?.actions ?? []
  const summary = report?.summary
  const featureKey = form.feature_key || features[0]?.key || ''

  const add = async () => {
    setFormError('')
    try {
      const res = await apiClient.post<Report>(BASE, {
        feature_key: featureKey, text: form.text.trim(), owner: form.owner.trim(), due: form.due,
      })
      setReport(res.data)
      setForm({ feature_key: form.feature_key, text: '', owner: '', due: '' })
    } catch (err) {
      setFormError(errorText(err))
    }
  }

  const save = async (id: string, status: string) => {
    setRowErrors(prev => ({ ...prev, [id]: '' }))
    try {
      const res = await apiClient.put<Report>(`${BASE}/${id}`, { status })
      setReport(res.data)
    } catch (err) {
      setRowErrors(prev => ({ ...prev, [id]: errorText(err) }))
    }
  }

  const set = (patch: Partial<typeof form>) => setForm(prev => ({ ...prev, ...patch }))

  return (
    <section data-testid="ai-risk-actions-section" className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900">Risk actions</h2>
      {error && <div className="text-sm text-red-600">{error}</div>}
      {loading && !report && <p className="text-sm text-gray-500">Loading…</p>}

      {summary && (
        <p data-testid="ra-summary" className="text-sm text-gray-700">
          {summary.open} open - {summary.in_progress} in progress - {summary.done} done
          - {summary.accepted} accepted - {summary.overdue} overdue
        </p>
      )}

      <div className="flex flex-wrap items-start gap-2">
        <select data-testid="ra-feature" className="rounded border px-2 py-1" value={featureKey}
          onChange={e => set({ feature_key: e.target.value })}>
          {features.map(f => <option key={f.key} value={f.key}>{f.name}</option>)}
        </select>
        <input type="text" data-testid="ra-text" placeholder="What will be done (max 500 characters)"
          maxLength={500} className="w-72 rounded border px-2 py-1" value={form.text}
          onChange={e => set({ text: e.target.value })} />
        <OwnerPicker owners={owners} testId="ra-owner" value={form.owner} onChange={owner => set({ owner })} />
        <input type="date" data-testid="ra-due" className="rounded border px-2 py-1" value={form.due}
          onChange={e => set({ due: e.target.value })} />
        <Button type="button" size="sm" data-testid="ra-add" onClick={add}>Add</Button>
      </div>
      {formError && <p data-testid="ra-error" className="text-sm text-red-600">{formError}</p>}

      {actions.length === 0 ? (
        report && <p data-testid="ra-empty" className="text-sm text-gray-500">No risk actions yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-600">
              <th>Feature</th><th>Action</th><th>Owner</th><th>Due</th><th>Status</th><th />
            </tr>
          </thead>
          <tbody>
            {actions.map(a => <ActionRow key={a.id} a={a} error={rowErrors[a.id]} onSave={save} />)}
          </tbody>
        </table>
      )}
    </section>
  )
}
